import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient } from '@angular/common/http';
import { Subscription, catchError, map } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { HelpersService } from "./helpers.service";

@Injectable()

export class LegacyMigrationService extends HelpersService {

    public waitMigrationState = true;
    private putLegacyUserStateSubscription$: Subscription = new Subscription;

    public legacyMigrationProcessFlow = {
        routes: [
            { path: '/legacy-migration/security-questions', state: 'LEGACY_INITIAL' },
            { path: '/legacy-migration/update-signature', state: 'LEGACY_SIGNATURE' },
            { path: '/dashboard', state: 'ONBOARDING_FINAL' }
        ]
    };

    constructor(router: Router, http: HttpClient, translate: TranslateService, dialog: MatDialog) {
        super(router, http, translate, dialog)
    }

    public updateLegacyUserState(currentState: string, data: any, redirectTo?: string): void {
        if (this.putLegacyUserStateSubscription$ instanceof Subscription) {
            this.putLegacyUserStateSubscription$.unsubscribe();
        }
        this.putLegacyUserStateSubscription$ = this.putUserState(currentState, data).subscribe(res => {
            const flow = this.legacyMigrationProcessFlow;
            const found = flow.routes.filter(e => e.state === res.state)[0];
            let path = '';
            if (found) {
                this.STATE_MACHINE = data.machine;
                if (redirectTo) {
                    this.router.navigateByUrl(redirectTo);
                }
                path = found.path;
                if (res.data && res.data.id) {
                    path += '/' + res.data.id;
                }
                this.router.navigateByUrl(path);
            } else {
                this.router.navigateByUrl("/legacy-migration/security-questions");
            }
        }, err => {
            this.router.navigateByUrl("/legacy-migration/security-questions");
        });
    }

    private fallbackCheckLegacyUserState() {
        this.getPendingAssets().subscribe(res => {
            let state;
            let data = {
                machine: this.STATE_MACHINE_DEFAULT
            };
            if (res && res.data && res.data.length) {
                const found = res.data.filter((el: any) => el.update_signature);
                if(found.length) {
                    state = "LEGACY_INITIAL";
                    this.updateLegacyUserState(state, data);
                } else {
                    state = "ONBOARDING_FINAL";
                    data.machine = this.STATE_MACHINE_B2C;
                    this.updateLegacyUserState(state, data);
                }
            } else {
                state = "ONBOARDING_FINAL";
                data.machine = this.STATE_MACHINE_B2C;
                this.updateLegacyUserState(state, data);
            }
            this.waitMigrationState = false;
        });
    }

    public retLegacyUserState(): void {
        this.getUserState().subscribe(res => {
            switch (res.state) {
                case "ONBOARDING_FINAL":
                    this.fallbackCheckLegacyUserState();
                break;
                default:
                    this.onRetLegacyUserState(res);
                break;
            }
            this.waitMigrationState = false;
        }, err => {
            this.fallbackCheckLegacyUserState();
        })
    }

    private onRetLegacyUserState(res: any) {
        const flow = this.legacyMigrationProcessFlow;
        const found = flow.routes.filter(e => e.state === res.state)[0];
        let path = '';
        if (found) {
            path = found.path;
            this.STATE_MACHINE = res.data.machine;
            if (res.data && res.data.id) {
                path += '/' + res.data.id;
            }
            this.router.navigateByUrl(path);
        } else {
            this.fallbackCheckLegacyUserState();
        }
    }

    private putAndRedirectLegacyState(currentState: string, currentData: any, path: string) {
        this.putUserState(currentState, currentData).subscribe(res => {
            this.router.navigateByUrl(path);
            return;
        }, err => {
            this.router.navigateByUrl(path);
            return;
        });
    }

    public undoLegacyUserState(): void {
        this.getUserState().subscribe(res => {
            if (res) {
                let found: boolean = false;
                let i: number = 0;
                let flow: any = this.legacyMigrationProcessFlow;
                for (let route of flow.routes) {
                    if (route.state === res.state && i > 0) {
                        found = true;
                        this.STATE_MACHINE = res.data.machine;
                        let path = flow.routes[i - 1].path;
                        if (res.data && res.data.id) {
                            path += '/' + res.data.id;
                        }
                        let currentState = flow.routes[i - 1].state;
                        this.putAndRedirectLegacyState(currentState, res.data, path);
                    }
                    i = i + 1;
                }
                if (!found) {
                    history.back();
                }
            } else {
                history.back();
            }
        }, err => {
            history.back();
        })
    }

    public getPendingAssets() {
        return this.http.get(`${this.config.BASEURL_ORDER_API}/v1/secured/assets`, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public getDocuments(assetId: string) {
        return this.http.get(`${this.config.BASEURL_ORDER_API}/v1/secured/assets/${assetId}/agreements`, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public getDocumentsModal(assetId: String, type: string) {
        return `${this.config.BASEURL_ORDER_API}/v1/secured/assets/${assetId}/download-agreement?documentType=${type}`;
    }

    public downloadDocuments(assetId: String, type: string) {
        return this.http.get(`${this.config.BASEURL_ORDER_API}/v1/secured/assets/${assetId}/download-agreement?documentType=${type}`, this.getHTTPHeaderPDF(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }

    public sendConsents(assetId: string, req: any) {
        return this.http.patch(`${this.config.BASEURL_ORDER_API}/v1/secured/assets/${assetId}/agreements `, req, this.getHTTPHeader(true)).pipe(
            map((res: any) => {
                return res;
            }),
            catchError(error => {
                return this.handleError(error);
            })
        )
    }
}
